import { DateTime } from 'luxon';
import { createContext, ReactNode, useContext, useMemo } from 'react';

import { useDataProvider } from '@work4all/data';

import { Project } from '@work4all/models/lib/Classes/Project.entity';
import { StatisticProjectControlling } from '@work4all/models/lib/Classes/StatisticProjectControlling.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

interface ProjectControllingDataProviderProps {
  projectList: Project[];
  children: ReactNode;
}

interface ProjectControllingDataContextType {
  data: StatisticProjectControlling[];
}

const ProjectControllingDataContext =
  createContext<ProjectControllingDataContextType | null>(null);
const PROJECT_EMPTY_LIST = [];

export const ProjectControllingDataProvider = ({
  projectList = PROJECT_EMPTY_LIST,
  children,
}: ProjectControllingDataProviderProps) => {
  const requestData: DataRequest = useMemo(() => {
    const jsDate = new Date();
    //TODO: WW-6639 - PROJECT CONTROLLING - add date range picker
    const startDate = DateTime.fromJSDate(jsDate).minus({ years: 10 });
    const endDate = startDate.plus({ years: 10 });

    const data: StatisticProjectControlling = {
      projectCode: null,
      ownWorkHoursActual: null,
      ownWorkCostsActual: null,
      ownWorkFromOfferHoursEstimated: null,
      ownWorkFromOfferCostsEstimated: null,
      ownWorkFromOrderHoursEstimated: null,
      ownWorkFromOrderCostsEstimated: null,
      ownWorkFromEstimationHoursEstimated: null,
      ownWorkFromEstimationCostsEstimated: null,
      revenueFromEstimation: null,
      revenueFromOffer: null,
      revenueFromOrder: null,
      revenueFromInvoice: null,
      externalWorkFromEstimationCostsEstimated: null,
      externalWorkFromOfferCostsEstimated: null,
      externalWorkFromOrderCostsEstimated: null,
      externalWorkOpenPurchaseorder: null,
      externalWorkCostsPlanned: null,
      externalWorkCostsActual: null,
      proformaIncomingInvoice: null,
    };
    return {
      entity: Entities.statisticProjectControlling,
      data: data,
      vars: {
        projectFilter: projectList
          .filter(Boolean)
          .map((project) => project?.id),
        rangeStart: startDate.toFormat('yyyy-MM-dd'),
        rangeEnd: endDate.toFormat('yyyy-MM-dd'),
      },
      completeDataResponse: true,
    };
  }, [projectList]);

  const { data } = useDataProvider(requestData);

  return (
    <ProjectControllingDataContext.Provider
      value={{
        data,
      }}
    >
      {children}
    </ProjectControllingDataContext.Provider>
  );
};

export const useProjectControllingData = () => {
  const context = useContext(ProjectControllingDataContext);
  if (!context) {
    throw new Error(
      'useProjectControllingData must be used within a ProjectControllingDataProvider'
    );
  }
  return context;
};
